import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle, PawPrint, AlertCircle, RefreshCw, Mail } from "lucide-react";
import { requestPushPermission } from "../services/notificationService";
import { toast } from "sonner";
import { supabase } from "../integrations/supabase/client";
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { signInSchema, signUpSchema } from "@/lib/validations/auth";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { ZodError } from "zod";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isServiceProvider, setIsServiceProvider] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralValidation, setReferralValidation] = useState<{
    valid: boolean;
    referrerName?: string;
    message: string;
  } | null>(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [signInError, setSignInError] = useState("");
  const [signUpError, setSignUpError] = useState("");
  const [lastSignUpEmail, setLastSignUpEmail] = useState("");
  const [showForgotPasswordSent, setShowForgotPasswordSent] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  
  // Field-specific validation errors
  const [signInErrors, setSignInErrors] = useState<Record<string, string>>({});
  const [signUpErrors, setSignUpErrors] = useState<Record<string, string>>({});
  const { 
    signIn, 
    signUp, 
    signInWithGoogle, 
    signInWithMicrosoft, 
    signInWithDiscord, 
    signInWithTwitter,
    signInWithFacebook,
    signInWithApple,
    resendVerificationEmail, 
    user 
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const { t } = useTranslation();

  // Fallback: verificar sesión periódicamente en plataformas nativas durante OAuth
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (Capacitor.isNativePlatform() && !user) {
      // Verificar cada segundo si hay sesión establecida (fallback para OAuth nativo)
      interval = setInterval(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            console.log('📱 Session detected via fallback check');
            if (interval) clearInterval(interval);
          }
        } catch (e) {
          // Silently ignore errors
        }
      }, 1000);
      
      // Limpiar después de 30 segundos para no consumir recursos indefinidamente
      const timeout = setTimeout(() => {
        if (interval) clearInterval(interval);
      }, 30000);
      
      return () => {
        if (interval) clearInterval(interval);
        clearTimeout(timeout);
      };
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user]);

  useEffect(() => {
    // Auto-detect referral code from URL parameter
    const urlParams = new URLSearchParams(location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      const upperCode = refCode.toUpperCase();
      setReferralCode(upperCode);
      validateReferralCode(upperCode);
    }
  }, [location.search]);

  useEffect(() => {
    // Check provider onboarding status and redirect accordingly
    const handlePostLoginRedirect = async () => {
      if (!user) return;

      try {
        // Check if user needs to complete provider registration
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('provider_intent, provider_registration_completed')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error checking provider status:', error);
        }

        // ✅ FIX: Add delay for iOS to recalculate safe areas after login
        const navigateTimeout = setTimeout(() => {
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            requestPushPermission().catch(err => 
              console.error('Error registering push on login:', err)
            );
          }

          // Redirect to provider registration if needed
          if (profile?.provider_intent && !profile?.provider_registration_completed) {
            navigate('/register-provider', { 
              replace: true, 
              state: { isOnboarding: true } 
            });
          } else {
            navigate(from, { replace: true });
          }
        }, Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' ? 150 : 0);
        
        return () => clearTimeout(navigateTimeout);
      } catch (error) {
        console.error('Error in post-login redirect:', error);
        // Fallback to normal redirect
        navigate(from, { replace: true });
      }
    };

    handlePostLoginRedirect();
  }, [user, navigate, from]);

  const handleBackToLanding = () => {
    navigate("/");
  };

  const getErrorMessage = (error: any): string => {
    const errorMessage = error.message?.toLowerCase() || '';
    
    if (errorMessage.includes('email not confirmed')) {
      setShowResendOption(true);
      return "Please check your email and click the verification link before signing in. If you can't find the email, you can resend it below.";
    }
    
    if (errorMessage.includes('invalid login credentials') || errorMessage.includes('invalid email or password')) {
      return "Invalid email or password. Please check your credentials and try again.";
    }
    
    if (errorMessage.includes('email rate limit exceeded')) {
      return "Too many requests. Please wait a moment before trying again.";
    }
    
    if (errorMessage.includes('signup is disabled')) {
      return "Account registration is currently disabled. Please contact support.";
    }
    
    if (errorMessage.includes('password should be at least')) {
      return "Password must be at least 6 characters long.";
    }
    
    if (errorMessage.includes('user already registered')) {
      return "An account with this email already exists. Please try signing in instead.";
    }
    
    if (errorMessage.includes('network')) {
      return "Network error. Please check your connection and try again.";
    }
    
    return error.message || "An unexpected error occurred. Please try again.";
  };

  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralValidation(null);
      return;
    }
    
    setIsValidatingReferral(true);
    try {
      const { data, error } = await supabase.rpc('validate_referral_code', {
        p_code: code.toUpperCase()
      });
      
      if (error) {
        setReferralValidation({ valid: false, message: t('referrals.errorValidating') });
        return;
      }
      
      setReferralValidation(data as any);
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralValidation({ valid: false, message: t('referrals.errorValidating') });
    } finally {
      setIsValidatingReferral(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSignInError("");
    setSignInErrors({});
    setShowResendOption(false);
    
    try {
      // Validate with Zod schema
      const validatedData = signInSchema.parse({ email, password });
      
      await signIn(validatedData.email, validatedData.password);
      // Will redirect in the useEffect
    } catch (error: any) {
      if (error instanceof ZodError) {
        // Handle Zod validation errors
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setSignInErrors(fieldErrors);
        toast.error("Please check your input and try again.");
      } else {
        // Handle Supabase errors
        console.error("Sign in error:", error);
        const errorMessage = getErrorMessage(error);
        setSignInError(errorMessage);
        toast.error(errorMessage);
        
        // Store email for potential resend verification
        if (error.message?.toLowerCase().includes('email not confirmed')) {
          setLastSignUpEmail(email);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSignUpError("");
    setSignUpErrors({});
    
    try {
      // Validate with Zod schema
      const validatedData = signUpSchema.parse({ 
        firstName, 
        lastName, 
        email, 
        password 
      });
      
      await signUp(
        validatedData.email, 
        validatedData.password, 
        validatedData.firstName, 
        validatedData.lastName, 
        isServiceProvider,
        referralCode || undefined
      );
      setLastSignUpEmail(validatedData.email);
      setShowVerificationMessage(true);
      toast.success("Account created successfully!", {
        description: "Please check your email for a verification link before signing in.",
        duration: 8000
      });
      // Clear form
      setEmail("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setIsServiceProvider(false);
      setReferralCode("");
      setReferralValidation(null);
    } catch (error: any) {
      if (error instanceof ZodError) {
        // Handle Zod validation errors
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setSignUpErrors(fieldErrors);
        toast.error("Please check your input and try again.");
      } else {
        // Handle Supabase errors
        console.error("Sign up error:", error);
        const errorMessage = getErrorMessage(error);
        setSignUpError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!lastSignUpEmail && !email) {
      toast.error("Please enter your email address first.");
      return;
    }
    
    const emailToUse = lastSignUpEmail || email;
    setIsResendingVerification(true);
    
    try {
      await resendVerificationEmail(emailToUse);
      toast.success("Verification email sent!", {
        description: "Please check your email for the new verification link.",
        duration: 5000
      });
      setShowResendOption(false);
    } catch (error: any) {
      console.error("Resend verification error:", error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      console.log("Starting Google sign in...");
      await signInWithGoogle();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("Google sign in error:", error);
      toast.error(error.message || "Failed to sign in with Google. Please try again.");
    }
  };


  const handleTwitterSignIn = async () => {
    try {
      console.log("Starting X.com sign in...");
      await signInWithTwitter();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("X.com sign in error:", error);
      toast.error(error.message || "Failed to sign in with X.com. Please try again.");
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      console.log("Starting Microsoft sign in...");
      await signInWithMicrosoft();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("Microsoft sign in error:", error);
      toast.error(error.message || "Failed to sign in with Microsoft. Please try again.");
    }
  };

  const handleDiscordSignIn = async () => {
    try {
      console.log("Starting Discord sign in...");
      await signInWithDiscord();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("Discord sign in error:", error);
      toast.error(error.message || "Failed to sign in with Discord. Please try again.");
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      console.log("Starting Facebook sign in...");
      await signInWithFacebook();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("Facebook sign in error:", error);
      toast.error(error.message || "Failed to sign in with Facebook. Please try again.");
    }
  };

  const handleAppleSignIn = async () => {
    try {
      console.log("Starting Apple sign in...");
      await signInWithApple();
      // The redirect will happen automatically through Supabase OAuth flow
    } catch (error: any) {
      console.error("Apple sign in error:", error);
      toast.error(error.message || "Failed to sign in with Apple. Please try again.");
    }
  };


  const handleForgotPassword = async () => {
    const emailToUse = email || forgotPasswordEmail;
    
    if (!emailToUse) {
      toast.error("Please enter your email address first.");
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(emailToUse, {
        redirectTo: `${window.location.origin}/update-password`
      });
      
      if (error) {
        throw error;
      }
      
      setForgotPasswordEmail(emailToUse);
      setShowForgotPasswordSent(true);
      toast.success("Password reset email sent!", {
        description: "Please check your email for the reset link.",
        duration: 5000
      });
    } catch (error: any) {
      console.error("Forgot password error:", error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show verification message if user just signed up
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen flex items-start justify-center bg-gray-50 px-4 pt-12 pb-8 safe-area-y">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-2xl text-green-700">Check Your Email!</CardTitle>
            <CardDescription>
              We've sent a verification link to <strong>{lastSignUpEmail}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-gray-600">
              Please click the verification link in your email to activate your account, then return here to sign in.
            </p>
            <p className="text-xs text-gray-500">
              Didn't receive an email? Check your spam folder or click below to resend.
            </p>
            <Button 
              onClick={handleResendVerification}
              disabled={isResendingVerification}
              variant="outline" 
              className="w-full"
            >
              {isResendingVerification ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={() => setShowVerificationMessage(false)}
              variant="ghost" 
              className="w-full"
            >
              Back to Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show forgot password sent message
  if (showForgotPasswordSent) {
    return (
      <div className="min-h-screen flex items-start justify-center bg-gray-50 px-4 pt-12 pb-8 safe-area-y">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Mail className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <CardTitle className="text-2xl text-blue-700">Check Your Email!</CardTitle>
            <CardDescription>
              We've sent a password reset link to <strong>{forgotPasswordEmail}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-gray-600">
              Please click the reset link in your email to create a new password.
            </p>
            <p className="text-xs text-gray-500">
              Didn't receive an email? Check your spam folder or try again.
            </p>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={() => setShowForgotPasswordSent(false)}
              variant="ghost" 
              className="w-full"
            >
              Back to Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 px-4 safe-area-top">
      <div className="flex-1 flex items-start justify-center pt-12 pb-8">
        <div className="w-full max-w-md">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToLanding}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('navigation.home')}
          </Button>

          <Card className="w-full relative">
          <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <h1 className="text-3xl font-bold text-pet-purple flex items-center">
              <div className="w-8 h-8 bg-pet-purple rounded-full flex items-center justify-center mr-2">
                <PawPrint className="h-4 w-4 text-white" />
              </div>
              <span className="petius-brand">Petius</span>
            </h1>
          </div>
          <CardTitle className="text-2xl text-center">{t('auth.welcome')}</CardTitle>
          <CardDescription className="text-center">
            {t('auth.subtitle')}
          </CardDescription>
        </CardHeader>
        
        <Tabs defaultValue="signin" className="w-full">
          <TabsList className="bg-gray-100 p-1 rounded-lg mb-4 grid grid-cols-2 gap-2">
            <TabsTrigger 
              value="signin" 
              className="px-3 py-3 rounded-md font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 hover:text-gray-900 text-sm min-h-[3rem] flex items-center justify-center"
              >
                {t('common.signIn')}
              </TabsTrigger>
            <TabsTrigger 
              value="signup" 
              className="px-3 py-3 rounded-md font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 hover:text-gray-900 text-sm min-h-[3rem] flex items-center justify-center"
              >
                {t('common.signUp')}
              </TabsTrigger>
          </TabsList>
          
          <TabsContent value="signin">
            <form onSubmit={handleSignIn}>
              <CardContent className="space-y-4">
                {signInError && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{signInError}</span>
                  </div>
                )}
                
                {showResendOption && (
                  <div className="p-3 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <span>Need to resend verification email?</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={isResendingVerification}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        {isResendingVerification ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          "Resend"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="signin-email">{t('auth.email')}</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setSignInErrors(prev => ({ ...prev, email: '' }));
                    }}
                    maxLength={255}
                    className={signInErrors.email ? 'border-red-500' : ''}
                    required
                  />
                  {signInErrors.email && (
                    <p className="text-xs text-red-600 mt-1">{signInErrors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">{t('auth.password')}</Label>
                    <Button 
                      type="button"
                      variant="link" 
                      className="p-0 h-auto text-xs"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                    >
                      {t('auth.forgotPassword')}
                    </Button>
                  </div>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setSignInErrors(prev => ({ ...prev, password: '' }));
                    }}
                    maxLength={72}
                    className={signInErrors.password ? 'border-red-500' : ''}
                    required
                  />
                  {signInErrors.password && (
                    <p className="text-xs text-red-600 mt-1">{signInErrors.password}</p>
                  )}
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-pet-purple hover:bg-pet-purple/90" 
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : t('common.signIn')}
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">
                      {t('auth.orContinueWith')}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleGoogleSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                        <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                        <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                        <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                        <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                      </g>
                    </svg>
                    Google
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleAppleSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Apple
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleFacebookSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleTwitterSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X.com
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleMicrosoftSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#00A1F1" d="M0 0h11v11H0z"/>
                      <path fill="#00BCF2" d="M13 0h11v11H13z"/>
                      <path fill="#00D427" d="M0 13h11v11H0z"/>
                      <path fill="#FFB900" d="M13 13h11v11H13z"/>
                    </svg>
                    Microsoft
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDiscordSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#5865F2" d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.196.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Discord
                  </Button>
                </div>
              </CardContent>
            </form>
          </TabsContent>
          
          <TabsContent value="signup">
            <form onSubmit={handleSignUp}>
              <CardContent className="space-y-4">
                {signUpError && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{signUpError}</span>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="signup-firstname">{t('auth.firstName')}</Label>
                    <Input
                      id="signup-firstname"
                      type="text"
                      placeholder={t('auth.firstNamePlaceholder')}
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setSignUpErrors(prev => ({ ...prev, firstName: '' }));
                      }}
                      maxLength={50}
                      className={signUpErrors.firstName ? 'border-red-500' : ''}
                      required
                    />
                    {signUpErrors.firstName && (
                      <p className="text-xs text-red-600 mt-1">{signUpErrors.firstName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-lastname">{t('auth.lastName')}</Label>
                    <Input
                      id="signup-lastname"
                      type="text"
                      placeholder={t('auth.lastNamePlaceholder')}
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setSignUpErrors(prev => ({ ...prev, lastName: '' }));
                      }}
                      maxLength={50}
                      className={signUpErrors.lastName ? 'border-red-500' : ''}
                      required
                    />
                    {signUpErrors.lastName && (
                      <p className="text-xs text-red-600 mt-1">{signUpErrors.lastName}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="signup-email">{t('auth.email')}</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setSignUpErrors(prev => ({ ...prev, email: '' }));
                    }}
                    maxLength={255}
                    className={signUpErrors.email ? 'border-red-500' : ''}
                    required
                  />
                  {signUpErrors.email && (
                    <p className="text-xs text-red-600 mt-1">{signUpErrors.email}</p>
                  )}
                 </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">{t('auth.password')}</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setSignUpErrors(prev => ({ ...prev, password: '' }));
                    }}
                    maxLength={72}
                    className={signUpErrors.password ? 'border-red-500' : ''}
                    required
                  />
                  {signUpErrors.password && (
                    <p className="text-xs text-red-600 mt-1">{signUpErrors.password}</p>
                  )}
                  <PasswordStrengthIndicator password={password} />
                </div>

                <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Checkbox 
                    id="service-provider"
                    checked={isServiceProvider}
                    onCheckedChange={(checked) => setIsServiceProvider(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor="service-provider" 
                      className="font-medium text-sm cursor-pointer leading-tight"
                    >
                      {t('auth.serviceProviderIntent')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('auth.serviceProviderIntentDescription')}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-referral-code">{t('referrals.enterCode')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-referral-code"
                      type="text"
                      placeholder="PETIUS-XXXXXX"
                      value={referralCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setReferralCode(value);
                        if (value.length >= 6) {
                          validateReferralCode(value);
                        } else {
                          setReferralValidation(null);
                        }
                      }}
                      maxLength={20}
                      className={
                        referralValidation?.valid 
                          ? 'border-green-500' 
                          : referralValidation && !referralValidation.valid 
                          ? 'border-red-500' 
                          : ''
                      }
                    />
                    {isValidatingReferral && (
                      <RefreshCw className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  {referralValidation?.valid && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      {t('referrals.validCode', { name: referralValidation.referrerName })}
                    </p>
                  )}
                  {referralValidation && !referralValidation.valid && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {referralValidation.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">{t('referrals.codeRequired')}</p>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-pet-purple hover:bg-pet-purple/90" 
                  disabled={isLoading}
                >
                  {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
                </Button>
                
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <Separator />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">
                      {t('auth.orContinueWith')}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleGoogleSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                        <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                        <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                        <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                        <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
                      </g>
                    </svg>
                    Google
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleAppleSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Apple
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleFacebookSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleTwitterSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X.com
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleMicrosoftSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#00A1F1" d="M0 0h11v11H0z"/>
                      <path fill="#00BCF2" d="M13 0h11v11H13z"/>
                      <path fill="#00D427" d="M0 13h11v11H0z"/>
                      <path fill="#FFB900" d="M13 13h11v11H13z"/>
                    </svg>
                    Microsoft
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDiscordSignIn}
                    className="flex items-center justify-center gap-2"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#5865F2" d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.196.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Discord
                  </Button>
                </div>
              </CardContent>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
        </div>
      </div>
      <div className="safe-area-bottom pb-4"></div>
    </div>
  );
};

export default Auth;